/**
 * Seam boundary for the acting viewer — WIRED to the API (ENG-180 W5). Derives
 * the `Viewer` (mirrors the API's PermissionContext) from the current user id,
 * `activeOrganization.role` (org-ADMIN → the resolver short-circuit), and the
 * user's team/role memberships in `permissionSubjects`. Memoized so the viewer
 * reference stays stable across renders (a fresh object would bust the provider
 * value, hidden-sets, and per-node access memos).
 */

import { useMemo } from "react";
import {
	useGetCurrentUserAndOrganizationsQuery,
	usePermissionSubjectsQuery,
} from "../../graphql/generated/types.gen";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import type { Viewer } from "../types";

export function useCurrentViewer(): Viewer {
	const { activeOrganization, activeOrganizationId } = useActiveOrganization();
	const organizationId = activeOrganizationId ?? "";

	const { data: userData } = useGetCurrentUserAndOrganizationsQuery();
	const { data: subjectsData } = usePermissionSubjectsQuery({
		variables: { organizationId },
		skip: organizationId === "",
	});

	const currentUserId = userData?.currentUser?.id ?? "";
	const isAdmin = (activeOrganization?.role ?? "").toUpperCase() === "ADMIN";

	return useMemo<Viewer>(() => {
		const me = subjectsData?.permissionSubjects.users.find(
			(u) => u.id === currentUserId,
		);
		return {
			userId: currentUserId,
			teamIds: me?.teamIds ?? [],
			roleId: me?.roleId ?? null,
			isAdmin,
		};
	}, [subjectsData, currentUserId, isAdmin]);
}
